import { getInitiativeById, Initiatives } from "./initiatives";
import { dateStr } from "./format";

export const ADMIN_ERROR_CATEGORIES = {
  OFFERS: "OFFERS",
  OFFER_REPLACEMENT: "OFFER REPLACEMENT",
  SUBMITTING_USER: "SUBMITTING USER",
  SUBMITTING_USER_REPLACEMENT: "USER REPLACEMENT",
  SHARE_APP_CREATION: "Share app creation",
  OTHER: "OTHER",
};

const OFFER_REPLACEMENT_PATTERNS = [
  /offer channel failed to load;\s*alternative offers used/i,
];

const SUBMITTING_USER_REPLACEMENT_PATTERNS = [
  /using substitute/i,
  /was not found;\s*using substitute/i,
];

const SUBMITTING_USER_NOT_FOUND_PATTERNS = [
  /no submitting user found and no eligible substitute/i,
  /no eligible substitute user found/i,
];

const SUBMITTING_USER_PATTERNS = [
  /submitting user/i,
  /substitute user/i,
  /configured roles/i,
];

const OFFERS_PATTERNS = [
  /error getting offers/i,
  /no offers/i,
  /offer catalog/i,
  /configured offer/i,
  /selected offer/i,
  /error getting limitations/i,
  /error getting categories/i,
  /error getting payments/i,
  /promotion offer/i,
];

const SHARE_APP_CREATION_PATTERNS = [
  /connected org/i,
  /elevated access token/i,
  /sharedapp/i,
  /shared app/i,
  /quicklink/i,
  /formurl/i,
  /form url/i,
  /api key/i,
  /store keys/i,
  /create.*app/i,
  /share app/i,
];

export function classifyAdminError(errorMessage) {
  const msg = String(errorMessage ?? "").trim();
  if (!msg) return ADMIN_ERROR_CATEGORIES.OTHER;

  if (SUBMITTING_USER_REPLACEMENT_PATTERNS.some((re) => re.test(msg))) {
    return ADMIN_ERROR_CATEGORIES.SUBMITTING_USER_REPLACEMENT;
  }
  if (OFFER_REPLACEMENT_PATTERNS.some((re) => re.test(msg))) {
    return ADMIN_ERROR_CATEGORIES.OFFER_REPLACEMENT;
  }
  if (SUBMITTING_USER_NOT_FOUND_PATTERNS.some((re) => re.test(msg))) {
    return ADMIN_ERROR_CATEGORIES.SUBMITTING_USER;
  }
  if (OFFERS_PATTERNS.some((re) => re.test(msg))) {
    return ADMIN_ERROR_CATEGORIES.OFFERS;
  }
  if (SUBMITTING_USER_PATTERNS.some((re) => re.test(msg))) {
    return ADMIN_ERROR_CATEGORIES.SUBMITTING_USER;
  }
  if (SHARE_APP_CREATION_PATTERNS.some((re) => re.test(msg))) {
    return ADMIN_ERROR_CATEGORIES.SHARE_APP_CREATION;
  }
  return ADMIN_ERROR_CATEGORIES.OTHER;
}

const ADMIN_ERROR_WARNING_STYLE = {
  backgroundColor: "#fff8e1",
  color: "#f57f17",
};

const ADMIN_ERROR_ERROR_STYLE = {
  backgroundColor: "#ffebee",
  color: "#c62828",
};

export function adminErrorCategoryStyle(category) {
  if (
    category === ADMIN_ERROR_CATEGORIES.SUBMITTING_USER_REPLACEMENT ||
    category === ADMIN_ERROR_CATEGORIES.OFFER_REPLACEMENT
  ) {
    return ADMIN_ERROR_WARNING_STYLE;
  }
  return ADMIN_ERROR_ERROR_STYLE;
}

export function truncateText(text, max = 24) {
  const value = String(text ?? "");
  if (value.length <= max) return value;
  return `${value.slice(0, max)}…`;
}

export const ADMIN_ERROR_LOG_FIELD_LABELS = {
  id: "Id",
  sharedAppId: "Shared App Id",
  requestId: "Request Id",
  publicFixLink: "Public fix link",
  error: "Error",
  formUrl: "GoodLink",
  clientId: "Client Id",
  integrator: "Integrator",
  organizationId: "Organization Id",
  initiative: "Initiative",
  requesterEmail: "Requester Email",
  partnerPrimaryContactEmail: "Partner Primary Contact",
  salesforceAccountOwnerEmail: "Salesforce Account Owner",
  salesforceId: "Salesforce Id",
  salesforceUrl: "Salesforce Link",
  accountName: "Account Name",
  slackChannelRoute: "Slack Channel Route",
  notifySlack: "Notify Slack",
  notifyEmail: "Email Sent",
  emailTemplateId: "Email Template",
  createdAt: "Created At",
  updatedAt: "Updated At",
};

export const ADMIN_ERROR_LOG_MODAL_FIELDS = [
  "clientId",
  "error",
  "accountName",
  "formUrl",
  "salesforceUrl",
  "salesforceId",
  "requesterEmail",
  "partnerPrimaryContactEmail",
  "salesforceAccountOwnerEmail",
  "sharedAppId",
  "requestId",
  "publicFixLink",
  "integrator",
  "organizationId",
  "initiative",
  "slackChannelRoute",
  "notifySlack",
  "notifyEmail",
  "createdAt",
  "updatedAt",
  "id",
];

export function isAdminErrorLogToday(createdAt, now = new Date()) {
  if (!createdAt) return false;
  const loggedAt = new Date(createdAt);
  if (Number.isNaN(loggedAt.getTime())) return false;
  return (
    loggedAt.getFullYear() === now.getFullYear() &&
    loggedAt.getMonth() === now.getMonth() &&
    loggedAt.getDate() === now.getDate()
  );
}

export function countAdminErrorLogsToday(logs = [], now = new Date()) {
  return logs.filter((log) => isAdminErrorLogToday(log.createdAt, now)).length;
}

/** YYYY-MM-DD for native date inputs (local timezone). */
export function getAdminErrorLogDateInputValue(date = new Date()) {
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) return "";
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Parse YYYY-MM-DD into a local Date at midnight. */
export function parseAdminErrorLogDateInput(value) {
  const [year, month, day] = String(value ?? "")
    .split("-")
    .map((part) => Number(part));
  if (!year || !month || !day) return new Date();
  return new Date(year, month - 1, day);
}

/** Local start/end of day as ISO strings for OpenSearch createdAt filters. */
export function getAdminErrorLogDayRange(selectedDate = new Date()) {
  const date =
    selectedDate instanceof Date
      ? selectedDate
      : parseAdminErrorLogDateInput(selectedDate);
  const start = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    0,
    0,
    0,
    0
  );
  const end = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
    999
  );
  return {
    startIso: start.toISOString(),
    endIso: end.toISOString(),
  };
}

/** Local start/end for an inclusive date range (YYYY-MM-DD). */
export function getAdminErrorLogDateRange(startDate, endDate) {
  const startValue = getAdminErrorLogDateInputValue(
    parseAdminErrorLogDateInput(startDate)
  );
  let endValue = getAdminErrorLogDateInputValue(
    parseAdminErrorLogDateInput(endDate)
  );
  if (startValue && endValue && startValue > endValue) {
    endValue = startValue;
  }
  const { startIso } = getAdminErrorLogDayRange(startValue);
  const { endIso } = getAdminErrorLogDayRange(endValue);
  return { startIso, endIso, startValue, endValue };
}

export function isAdminErrorLogDateRangeToday(
  startDate,
  endDate,
  now = new Date()
) {
  const today = getAdminErrorLogDateInputValue(now);
  const startValue = getAdminErrorLogDateInputValue(
    parseAdminErrorLogDateInput(startDate)
  );
  const endValue = getAdminErrorLogDateInputValue(
    parseAdminErrorLogDateInput(endDate)
  );
  return startValue === today && endValue === today;
}

export function formatAdminErrorLogDateRangeLabel(startDate, endDate) {
  const { startValue, endValue } = getAdminErrorLogDateRange(startDate, endDate);
  const startLabel = dateStr(parseAdminErrorLogDateInput(startValue));
  const endLabel = dateStr(parseAdminErrorLogDateInput(endValue));
  if (startValue === endValue) return startLabel;
  return `${startLabel} – ${endLabel}`;
}

export const ADMIN_ERROR_LOG_DATE_PRESETS = [
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "last7", label: "Last 7 days" },
  { id: "last30", label: "Last 30 days" },
];

function shiftAdminErrorLogDateInputValue(value, dayOffset) {
  const date = parseAdminErrorLogDateInput(value);
  date.setDate(date.getDate() + dayOffset);
  return getAdminErrorLogDateInputValue(date);
}

export function getAdminErrorLogPresetDateRange(presetId, now = new Date()) {
  const today = getAdminErrorLogDateInputValue(now);
  switch (presetId) {
    case "yesterday": {
      const yesterday = shiftAdminErrorLogDateInputValue(today, -1);
      return { startValue: yesterday, endValue: yesterday };
    }
    case "last7":
      return {
        startValue: shiftAdminErrorLogDateInputValue(today, -6),
        endValue: today,
      };
    case "last30":
      return {
        startValue: shiftAdminErrorLogDateInputValue(today, -29),
        endValue: today,
      };
    case "today":
    default:
      return { startValue: today, endValue: today };
  }
}

export function resolveAdminErrorLogDatePreset(startDate, endDate, now = new Date()) {
  const { startValue, endValue } = getAdminErrorLogDateRange(startDate, endDate);
  for (const preset of ADMIN_ERROR_LOG_DATE_PRESETS) {
    const range = getAdminErrorLogPresetDateRange(preset.id, now);
    if (range.startValue === startValue && range.endValue === endValue) {
      return preset.id;
    }
  }
  return "custom";
}

/** AppSync searchAdminErrorLogs filter for a local date range (+ optional client). */
export function buildAdminErrorLogSearchFilter({
  clientId = "",
  startDate = new Date(),
  endDate = new Date(),
  selectedDate,
} = {}) {
  const rangeStart = selectedDate ?? startDate;
  const rangeEnd = selectedDate ?? endDate;
  const { startIso, endIso } = getAdminErrorLogDateRange(rangeStart, rangeEnd);
  const filters = [
    { createdAt: { gte: startIso } },
    { createdAt: { lte: endIso } },
  ];
  const normalizedClientId = String(clientId ?? "").trim();
  if (normalizedClientId) {
    filters.unshift({ clientId: { eq: normalizedClientId } });
  }
  return filters.length === 1 ? filters[0] : { and: filters };
}

/** Short label for the error log table. */
export function initiativeTableLabel(initiativeId) {
  const ini = getInitiativeById(initiativeId);
  if (ini) {
    const channel = String(ini.channel || "").toLowerCase();
    if (channel === "goodlink") return "GoodLink";
    if (channel === "roofhub") return "RoofHub";
    if (channel === "goodleapplus") return "GoodLeap+";
    return ini.id;
  }
  const raw = String(initiativeId ?? "").trim();
  if (!raw) return "";
  if (raw === "GoodLink" || raw === "QuickLinksConnected") return "GoodLink";
  if (raw === "GoodLeapPlusRoofHub") return "RoofHub";
  if (raw === "GoodLeapPlusOther") return "GoodLeap+";
  if (raw === "SunRun") return "SunRun";
  return raw;
}

export function initiativeDisplayName(initiativeId) {
  const ini = getInitiativeById(initiativeId);
  if (ini) return ini.name;
  const raw = String(initiativeId ?? "").trim();
  if (!raw) return "";
  if (raw === "GoodLink") return Initiatives.QUICKLINKS_CONNECTED.name;
  if (raw === "SunRun") return "SunRun";
  return raw;
}

export function salesforceHref(log) {
  const url = String(log?.salesforceUrl ?? "").trim();
  if (url) return url;
  const id = String(log?.salesforceId ?? "").trim();
  if (!id) return "";
  return `https://goodleap.lightning.force.com/lightning/r/Account/${id}/view`;
}

export function sharedAppEditHref(sharedAppId) {
  const id = String(sharedAppId ?? "").trim();
  if (!id) return "";
  return `/edit/${id}`;
}

export function requestFormHref(requestId) {
  const id = String(requestId ?? "").trim();
  if (!id) return "";
  return `/request/${id}`;
}

/** Public page to fix T01/T02 submitting user errors (request id required). */
export function fixSubmittingUserHref(requestId, sharedAppId) {
  const id = String(requestId ?? "").trim();
  if (!id) return "";
  const base = `/fix-submitting-user/${encodeURIComponent(id)}`;
  const sharedId = String(sharedAppId ?? "").trim();
  if (!sharedId) return base;
  return `${base}?sharedAppId=${encodeURIComponent(sharedId)}`;
}

export function isSubmittingUserFixError(log) {
  const category = classifyAdminError(log?.error);
  return (
    category === ADMIN_ERROR_CATEGORIES.SUBMITTING_USER_REPLACEMENT ||
    category === ADMIN_ERROR_CATEGORIES.SUBMITTING_USER
  );
}

/**
 * Resolve a search box entry to a clientId using cached partner list.
 * @returns {{ clientId: string, matchedBy: string } | null}
 */
export function resolveSearchToClientId(query, appsOptions = []) {
  const q = String(query ?? "").trim();
  if (!q) return null;

  const lower = q.toLowerCase();
  const exactId = appsOptions.find(
    (option) => String(option.id ?? "").toLowerCase() === lower
  );
  if (exactId) {
    return { clientId: exactId.id, matchedBy: "clientId" };
  }

  const idContains = appsOptions.filter((option) =>
    String(option.id ?? "").toLowerCase().includes(lower)
  );
  if (idContains.length === 1) {
    return { clientId: idContains[0].id, matchedBy: "clientId" };
  }

  const nameMatches = appsOptions.filter((option) =>
    String(option.label ?? "").toLowerCase().includes(lower)
  );
  if (nameMatches.length === 1) {
    return { clientId: nameMatches[0].id, matchedBy: "accountName" };
  }
  if (nameMatches.length > 1) {
    const startsWith = nameMatches.find((option) =>
      String(option.label ?? "").toLowerCase().startsWith(lower)
    );
    if (startsWith) {
      return { clientId: startsWith.id, matchedBy: "accountName" };
    }
    return { clientId: nameMatches[0].id, matchedBy: "accountName" };
  }

  if (!/\s/.test(q)) {
    return { clientId: q, matchedBy: "clientId" };
  }

  return null;
}

/** Filter autocomplete options; adds a raw clientId option when input looks like an id. */
export function filterPartnerSearchOptions(query, appsOptions = [], limit = 50) {
  const q = String(query ?? "").trim();
  if (!q) return appsOptions.slice(0, limit);

  const lower = q.toLowerCase();
  const matches = appsOptions.filter(
    (option) =>
      String(option.id ?? "").toLowerCase().includes(lower) ||
      String(option.label ?? "").toLowerCase().includes(lower)
  );
  if (matches.length > 0) return matches.slice(0, limit);

  if (!/\s/.test(q)) {
    return [
      {
        id: q,
        label: `Client Id: ${q}`,
        key: `client-id-${q}`,
      },
    ];
  }

  return [];
}
